import { useEffect, useState } from "react";
import { Phone, ScanBarcode, Star, Truck, UserRound, Users } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { textOn } from "@/lib/mapStyle";

export function RoleBadge({ role }) {
  const principal = role === "principal";
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
        principal ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground border border-border"
      }`}
    >
      {principal ? <Truck size={11} /> : <Users size={11} />}
      {principal ? "Principal" : "Secondaire"}
    </span>
  );
}

export function TourneeTag({ code, tournee, defaut }) {
  const couleur = tournee?.couleur || "#888888";
  return (
    <span
      className="inline-flex items-center gap-1 font-mono text-xs font-semibold px-1.5 py-0.5 rounded"
      style={{ background: couleur, color: textOn(couleur) }}
      title={`${tournee?.libelle || ""}${defaut ? " — tournée par défaut" : ""}`}
    >
      {defaut && <Star size={10} fill="currentColor" />}
      {code}
    </span>
  );
}

const SCAN_TONES = {
  attribuee: "bg-primary/15 text-primary border-primary/40",
  attente_constatation: "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/40",
  preattribuee: "bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/40",
};

/** Pastille « Scan n° X » (couleur selon le statut de la remise). */
export function ScanChip({ scan, showTournee = false }) {
  if (!scan) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded border whitespace-nowrap ${SCAN_TONES[scan.statut] || SCAN_TONES.attribuee}`}
      title={`${scan.statut_label}${scan.tournee ? ` — tournée ${scan.tournee}` : ""}`}
      data-testid="scan-chip"
    >
      <ScanBarcode size={11} /> <span className="font-mono">{scan.numero}</span>
      {showTournee && scan.tournee && <span className="font-normal opacity-80">· T. {scan.tournee}</span>}
    </span>
  );
}

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString("fr-FR")} ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
}

/** Fiche livreur en lecture seule (carte, liste pour les non-admins). */
export function LivreurFiche({ livreur, tourneesByCode = {}, open, onClose, footer }) {
  if (!livreur) return null;
  const tel = livreur.telephone;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="livreur-fiche">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0">
              <UserRound size={22} />
            </div>
            <div className="min-w-0 text-left">
              <DialogTitle className="truncate">{livreur.prenom} {livreur.nom}</DialogTitle>
              <DialogDescription className="flex items-center gap-2 mt-1">
                <RoleBadge role={livreur.role} />
                {livreur.sst && <span className="text-xs">SST {livreur.sst}</span>}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Téléphone</p>
            {tel ? (
              <a href={`tel:${tel.replace(/\./g, "")}`} className="inline-flex items-center gap-2 font-mono text-base font-semibold hover:underline">
                <Phone size={15} /> {tel}
              </a>
            ) : <span className="text-muted-foreground">—</span>}
          </div>

          <div data-testid="fiche-scans">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Scan en cours</p>
            {livreur.scans_en_cours?.length ? (
              <div className="space-y-1.5">
                {livreur.scans_en_cours.map((sc) => (
                  <div key={sc.attribution_id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border px-2.5 py-2">
                    <ScanBarcode size={15} className="text-primary shrink-0" />
                    <span className="text-sm">Scan n° <b className="font-mono">{sc.numero}</b></span>
                    {sc.tournee && <TourneeTag code={sc.tournee} tournee={tourneesByCode[sc.tournee]} />}
                    <span className="text-xs text-muted-foreground ml-auto">
                      {sc.statut_label}{sc.signe_le ? ` le ${fmt(sc.signe_le)}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            ) : <span className="text-sm text-muted-foreground">Aucune scan en cours</span>}
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Tournées affectées</p>
            {livreur.tournees?.length ? (
              <div className="space-y-1.5">
                {livreur.tournees.map((code) => {
                  const t = tourneesByCode[code];
                  return (
                    <div key={code} className="flex items-center gap-2">
                      <TourneeTag code={code} tournee={t} defaut={code === livreur.tournee_defaut} />
                      <span className="text-xs text-muted-foreground truncate">{t?.libelle}</span>
                      {code === livreur.tournee_defaut && <span className="text-[10px] text-primary ml-auto shrink-0">par défaut</span>}
                    </div>
                  );
                })}
              </div>
            ) : <span className="text-muted-foreground">Aucune tournée</span>}
          </div>

          {livreur.notes && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Notes</p>
              <p className="whitespace-pre-line">{livreur.notes}</p>
            </div>
          )}
          {footer}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Livreurs d'une tournée (fiche tournée / fiche adresse sur la carte). */
export function LivreursDeTournee({ code, tourneesByCode }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [fiche, setFiche] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    setError("");
    api.get(`/livreurs/par-tournee/${encodeURIComponent(code)}`)
      .then(({ data }) => alive && setData(data))
      .catch((e) => alive && setError(errMsg(e)));
    return () => { alive = false; };
  }, [code]);

  const Row = ({ l }) => (
    <button
      type="button"
      onClick={() => setFiche(l)}
      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent text-left"
      data-testid={`livreur-row-${l.id}`}
    >
      <UserRound size={14} className="text-muted-foreground shrink-0" />
      <span className="text-sm font-medium truncate">{l.prenom} {l.nom}</span>
      {l.tournee_defaut === code && <Star size={11} className="text-primary shrink-0" fill="currentColor" title="Tournée par défaut" />}
      {(l.scans_en_cours || []).map((sc) => <ScanChip key={sc.attribution_id} scan={sc} showTournee={sc.tournee && sc.tournee !== code} />)}
      {l.telephone && <span className="ml-auto font-mono text-[11px] text-muted-foreground shrink-0">{l.telephone}</span>}
    </button>
  );

  const ouvrirChauffeur = async (chauffeurId) => {
    const connu = [...(data?.principaux || []), ...(data?.secondaires || [])].find((l) => l.id === chauffeurId);
    if (connu) { setFiche(connu); return; }
    try { const { data: l } = await api.get(`/livreurs/${chauffeurId}`); setFiche(l); } catch (e) { setError(errMsg(e)); }
  };

  return (
    <div className="border-t border-border pt-3" data-testid="livreurs-tournee">
      {data?.scans?.length > 0 && (
        <div className="mb-3" data-testid="scans-tournee">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Scans sur la tournée</p>
          {data.scans.map((sc) => (
            <button
              key={sc.attribution_id}
              type="button"
              onClick={() => sc.chauffeur?.id && ouvrirChauffeur(sc.chauffeur.id)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent text-left"
            >
              <ScanChip scan={sc} />
              <span className="text-sm font-medium truncate">{sc.chauffeur ? `${sc.chauffeur.prenom} ${sc.chauffeur.nom}` : "—"}</span>
              <span className="ml-auto text-[11px] text-muted-foreground shrink-0">
                {sc.statut === "preattribuee" ? "pré-attribuée" : sc.statut === "attente_constatation" ? "à constater" : fmt(sc.signe_le)}
              </span>
            </button>
          ))}
        </div>
      )}
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Livreurs</p>
      {error && <p className="text-xs text-destructive">{error}</p>}
      {!data && !error && <p className="text-xs text-muted-foreground">Chargement…</p>}
      {data && !data.principaux.length && !data.secondaires.length && (
        <p className="text-xs text-muted-foreground">Aucun livreur affecté à cette tournée.</p>
      )}
      {data?.principaux.length > 0 && (
        <div className="mb-2">
          <p className="text-xs font-semibold flex items-center gap-1.5 mb-0.5"><Truck size={12} className="text-primary" /> {data.principaux.length > 1 ? "Chauffeurs principaux" : "Chauffeur principal"}</p>
          {data.principaux.map((l) => <Row key={l.id} l={l} />)}
        </div>
      )}
      {data?.secondaires.length > 0 && (
        <div>
          <p className="text-xs font-semibold flex items-center gap-1.5 mb-0.5 text-muted-foreground"><Users size={12} /> {data.secondaires.length > 1 ? "Chauffeurs secondaires" : "Chauffeur secondaire"}</p>
          {data.secondaires.map((l) => <Row key={l.id} l={l} />)}
        </div>
      )}
      <LivreurFiche livreur={fiche} tourneesByCode={tourneesByCode} open={!!fiche} onClose={() => setFiche(null)}
        footer={<p className="text-[11px] text-muted-foreground border-t border-border pt-3">Fiche modifiable uniquement depuis l'onglet « Fiches chauffeurs » (administrateurs).</p>} />
    </div>
  );
}
